/**
 * <wt-hero-video>: silent background loop for wt-hero and wt-media-feature (snippets/wt-loop-video).
 *
 * Plays only where it is visible (CSS decides per breakpoint), never for visitors who asked for
 * reduced motion or data saving, and only after the page has loaded, so the video never competes with
 * the LCP image, CSS and scripts for bandwidth. The video has preload="none" and no poster: the
 * section's image stays visible underneath until the first frame plays (class is-playing).
 */
class WtHeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (/** @type {any} */ (navigator).connection?.saveData) return;

    this.toggle = this.querySelector('[data-video-toggle]');
    this.userPaused = false;
    this.video.addEventListener(
      'playing',
      () => {
        this.classList.add('is-playing');
        if (this.toggle) this.toggle.hidden = false;
      },
      { once: true }
    );
    this.toggle?.addEventListener('click', this.onToggle);

    this.ready = document.readyState === 'complete';
    if (!this.ready) window.addEventListener('load', this.onLoad, { once: true });

    this.isIntersecting = false;
    this.observer = new IntersectionObserver((entries) => {
      this.isIntersecting = entries.some((entry) => entry.isIntersecting);
      this.sync();
    });
    this.observer.observe(this);

    // Re-check when the layout breakpoint changes (CSS shows or hides the hero video at 750px).
    this.mediaQuery = window.matchMedia('(min-width: 750px)');
    this.mediaQuery.addEventListener('change', this.sync);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.toggle?.removeEventListener('click', this.onToggle);
    this.mediaQuery?.removeEventListener('change', this.sync);
    window.removeEventListener('load', this.onLoad);
  }

  onToggle = () => {
    if (!this.toggle) return;
    this.userPaused = !this.userPaused;
    // Toggle button: constant name ("Pause background video"), the state is aria-pressed (ARIA APG)
    this.toggle.setAttribute('aria-pressed', String(this.userPaused));
    this.sync();
  };

  onLoad = () => {
    const start = () => {
      this.ready = true;
      this.sync();
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
    else setTimeout(start, 300);
  };

  sync = () => {
    if (!this.video || !this.ready) return;
    if (this.isIntersecting && this.offsetParent !== null && !this.userPaused) {
      this.video.preload = 'auto';
      this.video.play().catch(() => {});
    } else {
      this.video.pause();
    }
  };
}

if (!customElements.get('wt-hero-video')) customElements.define('wt-hero-video', WtHeroVideo);
